import {
  acceptInvitationRequestSchema,
  acceptInvitationResponseSchema,
  createTeamInvitationsRequestSchema,
  createTeamInvitationsResponseSchema,
  teamListResponseSchema,
  updateMemberRoleRequestSchema,
  updateOwnMemberProfileRequestSchema,
  updateOwnMemberProfileResponseSchema,
  type AcceptInvitationResponse,
  type CreateTeamInvitationsResponse,
  type TeamInviteRole,
  type TeamListResponse,
  type UpdateOwnMemberProfileResponse,
} from "@multi-chat/shared";
import { apiDelete, apiGet, apiPatch, apiPost } from "./client.js";

export async function getTeam(
  organizationId: number,
  signal?: AbortSignal,
): Promise<TeamListResponse> {
  return teamListResponseSchema.parse(
    await apiGet(`/organizations/${organizationId}/team`, signal),
  );
}

export async function updateOwnMemberProfile(
  organizationId: number,
  displayName: string,
): Promise<UpdateOwnMemberProfileResponse> {
  const body = updateOwnMemberProfileRequestSchema.parse({ displayName });
  return updateOwnMemberProfileResponseSchema.parse(
    await apiPatch(`/organizations/${organizationId}/members/me`, body),
  );
}

export async function updateMemberDisplayName(
  organizationId: number,
  membershipId: number,
  displayName: string,
): Promise<UpdateOwnMemberProfileResponse> {
  const body = updateOwnMemberProfileRequestSchema.parse({ displayName });
  return updateOwnMemberProfileResponseSchema.parse(
    await apiPatch(
      `/organizations/${organizationId}/members/${membershipId}/display-name`,
      body,
    ),
  );
}

export async function updateMemberRole(
  organizationId: number,
  membershipId: number,
  role: TeamInviteRole,
): Promise<void> {
  const body = updateMemberRoleRequestSchema.parse({ role });
  await apiPatch(`/organizations/${organizationId}/members/${membershipId}`, body);
}

export async function removeMember(
  organizationId: number,
  membershipId: number,
): Promise<void> {
  await apiDelete(`/organizations/${organizationId}/members/${membershipId}`);
}

export async function createTeamInvitations(
  organizationId: number,
  invitations: { displayName: string; email: string; role: TeamInviteRole }[],
): Promise<CreateTeamInvitationsResponse> {
  const body = createTeamInvitationsRequestSchema.parse({ invitations });
  return createTeamInvitationsResponseSchema.parse(
    await apiPost(`/organizations/${organizationId}/invitations`, body),
  );
}

export async function resendTeamInvitation(
  organizationId: number,
  invitationId: number,
): Promise<void> {
  await apiPost(`/organizations/${organizationId}/invitations/${invitationId}/resend`, {});
}

export async function cancelTeamInvitation(
  organizationId: number,
  invitationId: number,
): Promise<void> {
  await apiPost(`/organizations/${organizationId}/invitations/${invitationId}/cancel`, {});
}

export async function listMyInvitations(signal?: AbortSignal): Promise<{
  invitations: {
    invitationId: number;
    organizationId: number;
    organizationName: string;
    displayName: string;
    role: TeamInviteRole;
    expiresAt: string;
  }[];
}> {
  return (await apiGet("/invitations/mine", signal)) as {
    invitations: {
      invitationId: number;
      organizationId: number;
      organizationName: string;
      displayName: string;
      role: TeamInviteRole;
      expiresAt: string;
    }[];
  };
}

export async function acceptMyInvitation(invitationId: number): Promise<AcceptInvitationResponse> {
  return acceptInvitationResponseSchema.parse(
    await apiPost(`/invitations/${invitationId}/accept`, {}),
  );
}

export async function acceptInvitation(token: string): Promise<AcceptInvitationResponse> {
  const body = acceptInvitationRequestSchema.parse({ token });
  return acceptInvitationResponseSchema.parse(await apiPost("/invitations/accept", body));
}
